import { useState, useMemo } from 'react'
import type { ComplianceViolation, PolicyCriticality } from '@/types'
import { refMatchesFramework } from '@/lib/frameworkUtils'
import { formatShortDate } from '@/lib/compliance-format'
import { ViolationStatusGlyph } from './ViolationRow'
import { Button, ButtonLink } from '@/components/carbon/Button'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { DataTable, type DataColumn } from '@/components/carbon/DataTable'
import { IconCheck, IconClose, IconExternal, IconInfo, IconWaiver } from '@/components/carbon/icons'
import { SegmentedMeter } from '@/components/carbon/SegmentedMeter'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { Tag } from '@/components/carbon/Tag'

interface FrameworkControlsListProps {
  policies: ComplianceViolation[]
  frameworkCode?: string
  /** Control-level counts from the framework score (e.g., 1/18) — authoritative */
  passedControls?: number
  totalControls?: number
  /** Optional partial-control count from the framework score (backend-authoritative) */
  partialControls?: number
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
}

type ControlStatusFilter = 'all' | 'passing' | 'failing' | 'unavailable' | 'partial' | 'expired'
type ControlStatus = 'passed' | 'waived' | 'failed' | 'data_unavailable' | 'partial'

/** A framework control with its constituent policy checks */
interface FrameworkControl {
  /** The full framework reference string used as the control key (matches backend exactly) */
  controlRef: string
  /** All policy checks mapped to this control */
  checks: ComplianceViolation[]
  /**
   * - "passed" = all checks Pass
   * - "waived" = no failures, at least one waiver, no data-unavailable
   * - "partial" = no failures, at least one data-unavailable AND at least one pass/waiver
   * - "data_unavailable" = only data-unavailable checks (no pass, no waiver, no failure)
   * - "failed" = any real (non-waived, non-DU) failure
   */
  status: ControlStatus
  passCount: number
  failCount: number
  waivedCount: number
  dataUnavailableCount: number
  /** Subset of waivedCount whose waiver has expired (server-flagged `isExpired`). */
  expiredCount: number
  /** Latest `expiresAt` across the control's expired-waiver checks. Drives the
   *  "Waiver expired {date}" tag on the control row. Undefined when no
   *  expired waivers exist on this control. */
  maxExpiryAt: string | undefined
  /** Earliest `expiresAt` across the control's *active* Temporary waivers.
   *  Drives the "Waived · expires {date}" hint on a fully-waived control row
   *  so the user sees when the earliest exception lapses. Undefined when no
   *  active Temporary waivers exist (e.g., only Permanent / Not Applicable). */
  minActiveExpiryAt: string | undefined
  highestCriticality: PolicyCriticality
}

const CRITICALITY_ORDER: Record<string, number> = { 'SHALL': 0, 'SHOULD': 1, 'MAY': 2 }

/**
 * A control's state as glyph plus word (DESIGN.md "Severity"): a failing
 * control takes its highest open tier's shape (Coral only for critical);
 * passing, partial, waived and no-data controls are neutral, with a check,
 * waiver or info glyph (D12).
 */
function ControlStatusGlyph({ control }: { control: FrameworkControl }) {
  if (control.status === 'failed') {
    return <SeverityGlyph level={severityFromApi(control.highestCriticality)} />
  }
  const neutral = {
    passed: { word: 'Passing', Icon: IconCheck },
    partial: { word: 'Partial', Icon: IconCheck },
    waived: { word: 'Waived', Icon: IconWaiver },
    data_unavailable: { word: 'No data', Icon: IconInfo },
  }[control.status]
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-text-secondary">
      <neutral.Icon className="text-[color:var(--icon-secondary)]" />
      {neutral.word}
    </span>
  )
}

/**
 * Full control inventory view for a framework, on the Carbon kit: a summary
 * line and a segmented meter (passing, partial, failing, no data), contained
 * tabs by control state with count tags, then a DataTable of controls whose
 * rows expand in place into their policy checks and the actions on each.
 *
 * Groups policy checks by their FULL framework reference string (matching the backend's
 * controlStatuses dictionary key). A control is "passed" only if ALL its checks have
 * status "Pass" (waived-only controls are shown as "waived", not "passed").
 */
export default function FrameworkControlsList({
  policies,
  frameworkCode,
  passedControls,
  totalControls,
  partialControls,
  onAddWaiver,
  onEditWaiver,
  onDeleteWaiver,
}: FrameworkControlsListProps) {
  const [controlFilter, setControlFilter] = useState<ControlStatusFilter>('all')

  // Build framework controls by grouping checks under their full framework reference string.
  // This mirrors the backend's controlStatuses dictionary which uses the verbatim ref string
  // (e.g., "MCSB IM-1", "NIST SP 800-53 IA-2") as the key.
  const controls = useMemo<FrameworkControl[]>(() => {
    if (!frameworkCode) return []

    // Map: full framework reference string → list of policy checks
    const controlMap = new Map<string, ComplianceViolation[]>()

    for (const policy of policies) {
      const refs = policy.frameworkReferences ?? []
      // A single policy may map to MULTIPLE controls within the same framework
      // (e.g., a policy references both "MCSB IM-1" and "MCSB IM-6").
      // The backend counts each reference as a separate control entry.
      let matched = false

      for (const ref of refs) {
        if (refMatchesFramework(ref, frameworkCode)) {
          if (!controlMap.has(ref)) controlMap.set(ref, [])
          controlMap.get(ref)!.push(policy)
          matched = true
        }
      }

      // Skip policies with no matching framework reference — don't create ghost controls
      if (!matched) continue
    }

    return Array.from(controlMap.entries())
      .map(([controlRef, checks]): FrameworkControl => {
        let passCount = 0, failCount = 0, waivedCount = 0, dataUnavailableCount = 0
        let expiredCount = 0
        let maxExpiryAt: string | undefined
        let minActiveExpiryAt: string | undefined
        let highestCriticality: PolicyCriticality = 'MAY'

        for (const c of checks) {
          if (c.status === 'Pass') passCount++
          else if (c.hasWaiver || c.status === 'Waived') waivedCount++
          else if (c.status === 'DataUnavailable') dataUnavailableCount++
          else {
            failCount++
            if ((CRITICALITY_ORDER[c.criticality] ?? 2) < (CRITICALITY_ORDER[highestCriticality] ?? 2)) {
              highestCriticality = c.criticality
            }
          }

          // Track expired waivers separately so the control row + filter tab
          // can call them out. We compare by raw ISO string so locale parsing
          // can't flip the "latest" winner across timezones.
          if (c.waiver?.isExpired === true) {
            expiredCount++
            const expiresAt = c.waiver.expiresAt
            if (expiresAt && (!maxExpiryAt || expiresAt > maxExpiryAt)) {
              maxExpiryAt = expiresAt
            }
          }

          // Track the earliest active Temporary expiration so the "Waived"
          // tag on a fully-waived control can hint when the first exception
          // lapses. Permanent / Not Applicable waivers have no expiresAt and
          // are intentionally skipped — they're not on a timer.
          if (
            c.waiver &&
            !c.waiver.isExpired &&
            c.waiver.waiverType === 'Temporary' &&
            c.waiver.expiresAt
          ) {
            if (!minActiveExpiryAt || c.waiver.expiresAt < minActiveExpiryAt) {
              minActiveExpiryAt = c.waiver.expiresAt
            }
          }
        }

        // Match backend logic (five states):
        // - "failed" = any real (non-waived, non-data-unavailable) failures
        // - "partial" = no failures AND at least one data-unavailable check AND at least one pass/waiver
        // - "data_unavailable" = only data-unavailable checks
        // - "waived" = no failures, no data-unavailable, at least one waiver
        // - "passed" = all checks pass
        let status: ControlStatus
        if (failCount > 0) status = 'failed'
        else if (dataUnavailableCount > 0 && (passCount > 0 || waivedCount > 0)) status = 'partial'
        else if (dataUnavailableCount > 0) status = 'data_unavailable'
        else if (waivedCount > 0) status = 'waived'
        else status = 'passed'

        return {
          controlRef,
          checks: checks.sort((a, b) => a.policyCode.localeCompare(b.policyCode)),
          status,
          passCount,
          failCount,
          waivedCount,
          dataUnavailableCount,
          expiredCount,
          maxExpiryAt,
          minActiveExpiryAt,
          highestCriticality,
        }
      })
      .sort((a, b) => a.controlRef.localeCompare(b.controlRef))
  }, [policies, frameworkCode])

  // Use backend-authoritative counts when available, falling back to client aggregation.
  const displayPassed = passedControls ?? controls.filter(c => c.status === 'passed').length
  const displayTotal = totalControls ?? controls.length
  const displayUnavailable = controls.filter(c => c.status === 'data_unavailable').length
  const displayPartial = partialControls ?? controls.filter(c => c.status === 'partial').length
  const displayEvaluatable = displayTotal - displayUnavailable
  // Failing = evaluatable minus the buckets that count as passing (pure pass + partial).
  const displayFailing = Math.max(0, displayEvaluatable - displayPassed - displayPartial)

  // Total expired-waiver checks across the framework — the filter tab count
  // sums per-check occurrences, not per-control, so the user sees the same
  // number they'd encounter scrolling the expanded controls.
  const expiredTotal = useMemo(
    () => controls.reduce((sum, c) => sum + c.expiredCount, 0),
    [controls],
  )

  // Filter controls for list display
  const filteredControls = useMemo(() => {
    if (controlFilter === 'all') return controls
    if (controlFilter === 'passing') return controls.filter(c => c.status === 'passed')
    if (controlFilter === 'partial') return controls.filter(c => c.status === 'partial')
    if (controlFilter === 'unavailable') return controls.filter(c => c.status === 'data_unavailable')
    if (controlFilter === 'expired') return controls.filter(c => c.expiredCount > 0)
    // "failing" includes failed and waived-only (anything not passed/partial/data_unavailable)
    return controls.filter(c => c.status === 'failed' || c.status === 'waived')
  }, [controls, controlFilter])

  const filterTabs: { id: ControlStatusFilter; label: string; count: number }[] = [
    { id: 'all', label: 'All controls', count: displayTotal },
    { id: 'passing', label: 'Passing', count: displayPassed },
    { id: 'failing', label: 'Failing', count: displayFailing },
    ...(displayPartial > 0 ? [{ id: 'partial' as const, label: 'Partial', count: displayPartial }] : []),
    ...(displayUnavailable > 0 ? [{ id: 'unavailable' as const, label: 'No data', count: displayUnavailable }] : []),
    // The expired-waiver tab sits at the end, hidden until there's something to surface.
    ...(expiredTotal > 0 ? [{ id: 'expired' as const, label: 'Expired waivers', count: expiredTotal }] : []),
  ]

  const columns: DataColumn<FrameworkControl>[] = [
    {
      id: 'status',
      header: 'Status',
      nowrap: true,
      width: '9rem',
      cell: (control) => <ControlStatusGlyph control={control} />,
    },
    {
      id: 'control',
      header: 'Control',
      primary: true,
      cell: (control) => (
        <>
          <b className="block font-mono text-[13px] font-semibold text-text-primary">{control.controlRef}</b>
          <small className="mt-0.5 block text-helper tabular-nums text-text-helper">
            {control.passCount}/{control.checks.length} checks passing
          </small>
        </>
      ),
    },
    {
      id: 'state',
      header: 'Waivers and data',
      hideBelow: 'c-md',
      cell: (control) => <ControlStateTags control={control} />,
    },
  ]

  return (
    <div className="min-w-0 space-y-6">
      <div className="space-y-2">
        <p className="m-0 text-body text-text-primary">
          <b className="font-semibold tabular-nums">
            {displayPassed + displayPartial} of {displayEvaluatable}
          </b>{' '}
          controls passing
          {displayPartial > 0 && <span className="text-text-secondary"> (including {displayPartial} partial)</span>}
        </p>
        <SegmentedMeter
          caption="Controls by state"
          segments={[
            { id: 'passing', value: displayPassed, tone: 'bpa', label: 'passing' },
            { id: 'partial', value: displayPartial, tone: 'low', label: 'partial' },
            { id: 'failing', value: displayFailing, tone: 'strong', label: 'failing' },
            { id: 'unavailable', value: displayUnavailable, tone: 'off', label: 'no data' },
          ]}
        />
        <p className="m-0 text-helper text-text-helper">
          Each control may have several policy checks. A partial control passes on the checks Guardian could evaluate,
          and has more checks whose data wasn&rsquo;t available (a required license, for one).
        </p>
      </div>

      <DataTable<FrameworkControl>
        aria-label={`${frameworkCode ?? 'Framework'} controls`}
        columns={columns}
        rows={filteredControls}
        rowId={(c) => c.controlRef}
        rowLabel={(c) => c.controlRef}
        empty="No controls match this filter."
        tabs={
          <ContainedTabs
            aria-label="Control state"
            value={controlFilter}
            onChange={(id) => setControlFilter(id as ControlStatusFilter)}
            tabs={filterTabs}
          />
        }
        expandable={{
          render: (control) => (
            <ControlChecks
              control={control}
              onAddWaiver={onAddWaiver}
              onEditWaiver={onEditWaiver}
              onDeleteWaiver={onDeleteWaiver}
            />
          ),
        }}
      />
    </div>
  )
}

/** The control's waiver and data tags (expired waiver, waived, partial, no data). */
function ControlStateTags({ control }: { control: FrameworkControl }) {
  return (
    <span className="flex flex-wrap gap-1">
      {control.expiredCount > 0 && (
        <Tag
          size="sm"
          kind="high-contrast"
          title="One or more waivers on this control have expired. Renew or remove them to update the finding."
        >
          Waiver expired {formatShortDate(control.maxExpiryAt)}
        </Tag>
      )}
      {control.status === 'waived' && (
        // When any active waiver on the control is Temporary, surface the
        // earliest expiry inline so the user sees when the first exception
        // lapses without expanding the control.
        <Tag
          size="sm"
          title={
            control.minActiveExpiryAt
              ? control.waivedCount > 1
                ? `Earliest temporary exception on this control expires ${formatShortDate(control.minActiveExpiryAt)}`
                : `Temporary exception expires ${formatShortDate(control.minActiveExpiryAt)}`
              : undefined
          }
        >
          {control.minActiveExpiryAt ? `Waived · expires ${formatShortDate(control.minActiveExpiryAt)}` : 'Waived'}
        </Tag>
      )}
      {control.status === 'partial' && (
        <Tag
          size="sm"
          title={`${control.dataUnavailableCount} of ${control.checks.length} checks could not be evaluated (a required license or data was missing)`}
        >
          Passed · {control.dataUnavailableCount} not evaluated
        </Tag>
      )}
      {control.status === 'data_unavailable' && <Tag size="sm">No data</Tag>}
    </span>
  )
}

/** A control's policy checks, inside its expanded row: one block per check with its actions. */
function ControlChecks({
  control,
  onAddWaiver,
  onEditWaiver,
  onDeleteWaiver,
}: {
  control: FrameworkControl
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
}) {
  return (
    <div className="min-w-0">
      <h3 className="m-0 mb-2 text-body font-semibold text-text-primary">
        Policy checks <span className="font-normal tabular-nums text-text-helper">({control.checks.length})</span>
      </h3>
      {/* The state column hides below c-md, so its tags move into the detail there. */}
      <div className="mb-3 c-md:hidden">
        <ControlStateTags control={control} />
      </div>
      <ul className="m-0 list-none border-t border-line-subtle p-0">
        {control.checks.map(check => {
          const canAddWaiver = (check.status === 'Open' || check.status === 'DataUnavailable') && !check.hasWaiver && !!onAddWaiver
          return (
            <li key={check.id} className="border-b border-line-subtle py-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <ViolationStatusGlyph violation={check} />
                <span className="font-mono text-helper text-text-secondary">{check.policyCode}</span>
                <Tag kind="outline" size="sm">
                  {check.criticality}
                </Tag>
                {check.hasWaiver &&
                  (check.waiver?.isExpired ? (
                    // An expired waiver must not read as "still protected".
                    <Tag size="sm" kind="high-contrast" title="This waiver has expired. The check is back in the failing pool.">
                      Waiver expired {formatShortDate(check.waiver.expiresAt)}
                    </Tag>
                  ) : check.waiver?.waiverType === 'Temporary' && check.waiver.expiresAt ? (
                    <Tag size="sm" title={`Temporary exception expires ${formatShortDate(check.waiver.expiresAt)}`}>
                      Waived · expires {formatShortDate(check.waiver.expiresAt)}
                    </Tag>
                  ) : (
                    <Tag size="sm">Waived</Tag>
                  ))}
              </div>
              <p className="m-0 mt-1 text-body font-semibold text-text-primary">{check.policyTitle}</p>
              <p className="m-0 mt-0.5 line-clamp-2 text-body text-text-secondary">{check.message}</p>
              {check.waiver?.isExpired && (
                <p className="m-0 mt-0.5 text-helper text-text-secondary">
                  This waiver has expired. Renew or remove it to update the finding.
                </p>
              )}

              {check.frameworkReferences?.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  {check.frameworkReferences.map((ref, i) => (
                    <Tag key={i} kind="outline" size="sm">
                      {ref}
                    </Tag>
                  ))}
                </div>
              )}

              {check.status !== 'Pass' && check.remediationSteps && (
                <div className="mt-2">
                  <p className="m-0 text-helper text-text-helper">How to fix it</p>
                  <p className="m-0 mt-0.5 text-body text-text-secondary">{check.remediationSteps}</p>
                </div>
              )}

              {((check.remediationUrl && check.status !== 'Pass') || canAddWaiver || check.hasWaiver) && (
                <div className="-ml-3 mt-2 flex flex-wrap items-center gap-1">
                  {check.remediationUrl && check.status !== 'Pass' && (
                    <ButtonLink href={check.remediationUrl} kind="ghost" size="sm" icon={<IconExternal />}>
                      Microsoft Learn
                    </ButtonLink>
                  )}
                  {/* No-data controls are waivable too (#94) — same gate as ViolationRow. */}
                  {canAddWaiver && (
                    <Button kind="ghost" size="sm" icon={<IconWaiver />} onClick={() => onAddWaiver?.(check)}>
                      Add waiver
                    </Button>
                  )}
                  {check.hasWaiver && onEditWaiver && (
                    <Button kind="ghost" size="sm" icon={<IconWaiver />} onClick={() => onEditWaiver(check)}>
                      {check.waiver?.isExpired ? 'Renew waiver' : 'Edit waiver'}
                    </Button>
                  )}
                  {check.hasWaiver && onDeleteWaiver && (
                    <Button kind="ghost" size="sm" icon={<IconClose />} onClick={() => onDeleteWaiver(check)}>
                      Remove waiver
                    </Button>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
